// src/contexts/LocationContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { getUserPermission, saveUserPermission } from "@/lib/session-utils";

// Define the context type
interface LocationContextType {
  locationPermission: "granted" | "denied" | "prompt" | "loading";
  requestPermission: () => Promise<boolean>;
  currentLocation: GeolocationCoordinates | null;
  locationError: string | null;
}

// Create the context with a default value
const LocationContext = createContext<LocationContextType | undefined>(
  undefined
);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [locationPermission, setLocationPermission] = useState<
    "granted" | "denied" | "prompt" | "loading"
  >("loading");
  const [currentLocation, setCurrentLocation] =
    useState<GeolocationCoordinates | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Check permission status on component mount
  useEffect(() => {
    const checkPermission = async () => {
      try {
        // Check if we have a saved permission choice
        const savedPermission = await getUserPermission("geolocation");

        if (savedPermission === "granted" || savedPermission === "denied") {
          setLocationPermission(savedPermission);

          // If permission was granted, try to get the location
          if (savedPermission === "granted") {
            getLocation();
          }
        } else {
          setLocationPermission("prompt");
        }
      } catch (error) {
        console.error("Error checking location permission:", error);
        setLocationPermission("prompt");
      }
    };

    checkPermission();
  }, []);

  // Function to get current location
  const getLocation = (): Promise<GeolocationCoordinates | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser");
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation(position.coords);
          setLocationError(null);
          resolve(position.coords);
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setLocationError("User denied the request for geolocation");
              setLocationPermission("denied");
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationError("Location information is unavailable");
              break;
            case error.TIMEOUT:
              setLocationError("The request to get user location timed out");
              break;
            default:
              setLocationError("An unknown error occurred");
          }
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000, // Cache for 1 minute
        }
      );
    });
  };

  // Function to request location permission with improved handling
  const requestPermission = async (): Promise<boolean> => {
    try {
      // If already granted, just return true
      if (locationPermission === "granted") {
        return true;
      }

      // If previously denied and saved in persistent storage, don't prompt again
      if (locationPermission === "denied") {
        console.log(
          "Location permission was previously denied - not prompting again"
        );
        return false;
      }

      // Check if the browser supports the permissions API
      if (navigator.permissions && navigator.permissions.query) {
        try {
          // Check current permission state
          const permissionStatus = await navigator.permissions.query({
            name: "geolocation",
          });

          // If permission is already granted, update our state and return
          if (permissionStatus.state === "granted") {
            setLocationPermission("granted");
            await saveUserPermission("geolocation", "granted");
            return true;
          }

          // If permission is already denied, update our state and return
          if (permissionStatus.state === "denied") {
            setLocationPermission("denied");
            await saveUserPermission("geolocation", "denied");
            return false;
          }

          // If we get here, permission state is 'prompt', so we'll continue below
          console.log("Browser will prompt for location permission");
        } catch (permError) {
          console.error("Error checking permission status:", permError);
          // Continue with the regular flow below if permission check fails
        }
      }

      // Request the permission by trying to get the location
      const coords = await getLocation();

      // Save the user's choice
      if (coords) {
        setLocationPermission("granted");
        setCurrentLocation(coords);
        await saveUserPermission("geolocation", "granted");
        return true;
      } else {
        // If there was an error getting location, it might be due to permission denial
        setLocationPermission("denied");
        await saveUserPermission("geolocation", "denied");
        return false;
      }
    } catch (error) {
      console.error("Error requesting location permission:", error);
      setLocationPermission("denied");
      await saveUserPermission("geolocation", "denied");
      return false;
    }
  };

  // Create the context value
  const contextValue: LocationContextType = {
    locationPermission,
    requestPermission,
    currentLocation,
    locationError,
  };

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use the location context
export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};
